import React, { forwardRef, HTMLProps, MouseEvent } from "react";
import styled from "styled-components/macro";

import { ExternalLink as ExternalLinkIcon } from "ui/Icons";

import { ActionType } from "utils/analytics/analyticsConstants";
import { useAnalytics } from "utils/analytics/AnalyticsContext";
import { AnalyticsProps } from "utils/analytics/AnalyticsProps";

interface LinkProps extends AnalyticsProps, HTMLProps<HTMLAnchorElement> {
  isOnDarkBackground?: boolean;
  opensInNewTab?: boolean;
  noExternalLinkIcon?: boolean;
}

// We're using em units for this icon's styling because so it scales
// with the link's font size.
const $ExternalLinkIconWrapper = styled.span`
  margin-left: 0.1em;

  svg {
    width: 0.8em;
    height: 0.8em;
  }
`;

/**
 * If the link opens in a new tab, add either a visual cue (external link icon)
 * with a screen-readable title, or screen-reader-only text to indicate the
 * behavior.
 *
 * https://codersblock.com/blog/external-links-new-tabs-and-accessibility/
 */
function getLinkChildren({
  children,
  opensInNewTab,
  noExternalLinkIcon,
}: LinkProps) {
  if (!opensInNewTab) {
    return children;
  }

  const accessibilityElement = noExternalLinkIcon ? (
    <span className="visually-hidden">Opens in a new tab.</span>
  ) : (
    <$ExternalLinkIconWrapper>
      <ExternalLinkIcon role="img" title="Opens in a new tab." />
    </$ExternalLinkIconWrapper>
  );

  return (
    <>
      {children}
      {accessibilityElement}
    </>
  );
}

/**
 * Helper to correctly consume and apply component props.
 */
function useLinkProps({
  analyticsName,
  analyticsSurface,
  analyticsProperties,
  opensInNewTab,
  rel,
  target,
  // Omit the following to prevent applying them to the `<a>` element.
  isOnDarkBackground,
  noExternalLinkIcon,
  ...props
}: LinkProps) {
  const { trackEvent } = useAnalytics("Link " + analyticsName);

  let href = props.href;
  let role = props.role;

  // Make sure there's an href and role attribute on the node. Otherwise,
  // styling and cursor behaviors disappear.
  if (!href || href.trim() === "#") {
    href = "#";
    role = "button";
  }

  if (opensInNewTab) {
    // If the link opens in a new tab, we assume it's external and that it's
    // okay to automatically include the href URL in the click event metadata
    analyticsProperties = { ...analyticsProperties, href };
  }

  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (href === "#") {
      e.preventDefault();
    }

    trackEvent({
      eventName: analyticsName,
      eventType: ActionType.CLICK_LINK,
      analyticsSurface,
      extraProperties: analyticsProperties,
    });

    props.onClick && props.onClick(e);
  };

  return {
    ...props,
    href,
    onClick,
    // Default to "noopener" when opening in new tab, but allow other values.
    // https://mathiasbynens.github.io/rel-noopener/
    rel: opensInNewTab && !rel ? "noopener" : rel,
    role,
    // If `opensInNewTab` is `true`, `target` will be set to `"_blank"`.
    // This cannot be overriden by passing in a different `target`.
    target: opensInNewTab ? "_blank" : target,
  };
}

/**
 * Basic, unstyled link that incorporates analytics and default link behaviors.
 */
export const BaseLink = forwardRef<HTMLAnchorElement, LinkProps>(
  (props, ref) => (
    <a {...useLinkProps(props)} ref={ref}>
      {getLinkChildren(props)}
    </a>
  ),
);

BaseLink.displayName = "BaseLink"; // Satisfy eslint.

export const $BaseLink = styled(BaseLink)`
  text-decoration: none;
`;

/**
 * Styled version of the basic link. Should be used in most situations where a
 * standard link is needed and that doesn't involve router navigation.
 */
const $Link = styled(BaseLink).withConfig({
  shouldForwardProp: (prop) => !["isOnDarkBackground"].includes(prop),
})<LinkProps>`
  color: inherit;
  text-decoration: underline;

  &:active,
  &:focus,
  &:hover {
    color: ${({ isOnDarkBackground, theme }) =>
      isOnDarkBackground ? theme.ColorWhite : theme.ColorBlurple500};
  }

  // Icons should inherit the text color by default.
  path {
    fill: currentColor;
  }
`;

export default $Link;
